/*
    What every column means (MPN-1688, rewritten to the updated prototype for MPN-2044).

    Leadership reads this report, and the most likely failure is not a wrong number: it is a
    right number nobody believes. The Google Sheet this replaces had its formulas visible in
    the cells. Hiding them behind a rewrite would be a step backwards, so every definition is
    reachable from the column it describes.

    SHORT, DELIBERATELY. These were several paragraphs each, carrying the build notes and the
    reasoning behind choices that could look like bugs. That is the right content for a code
    comment and the wrong content for a popover somebody opened mid-sentence to check one
    figure -- "Remove the longer build and methodology notes from the bubbles" (Holly, QA,
    7 Oct 2026). The shape is now the prototype's: a title that is usually the formula, an
    optional list of what each value means, then one or two plain sentences.

    The rationale did not go anywhere. It lives in the module that implements each figure,
    next to the code it explains, which is where it stays true.

    Kept as data rather than markup so the same entry renders in two places: inline on the
    Overview, and in the popover behind a column header. Two copies of this text would drift,
    and the drifted one would be the one quoted in a board deck.
*/

export interface MethodValue {
    /** The value, as it reads on screen. */
    name: string;
    /** What it means, in a few words. */
    means: string;
}

export interface MethodEntry {
    /** The heading. Usually the formula, because the formula is the answer most of the time. */
    field: string;
    /** What each value of a vocabulary column means. Absent for the numeric ones. */
    values?: MethodValue[];
    /** One or two plain sentences. Never a paragraph. */
    body: string;
}

const V = (pairs: [string, string][]): MethodValue[] =>
    pairs.map(([name, means]) => ({ name, means }));

export const METHODOLOGY: MethodEntry[] = [
    {
        field: "One row per campaign + Target Action",
        body: "A campaign tracking two Target Actions shows as two rows. Dimensions on a "
            + "Target Action roll into a single row.",
    },
    {
        field: "Campaign Status",
        values: V([
            ["Live", "still collecting data"],
            ["Paused", "stopped, may resume"],
            ["Ended", "final"],
        ]),
        body: "A Live row can still change. An Ended row is locked in.",
    },
    {
        field: "Funnel Stage = mapped from Use Case",
        body: "Set on the Use Case Mapping tab, not per campaign.",
    },
    {
        field: "Days Running = today (or end date) − start date",
        body: "A high lift after a short run reads differently from the same lift after months.",
    },
    {
        field: "Treatment Size = population − holdout",
        body: "Everyone eligible to receive the campaign.",
    },
    {
        field: "Control Size = Held Out group",
        body: "Members deliberately not messaged. They are the baseline.",
    },
    {
        field: "Treatment Conversions = conversions in the messaged group",
        body: "The count behind the treatment rate. Distinct people, not events — somebody who "
            + "converted twice counts once.",
    },
    {
        field: "Control Conversions = conversions in the held-out group",
        body: "The count behind the control rate. These people were never messaged, so they "
            + "are what would have happened anyway.",
    },
    {
        field: "Treatment Conv. = treatment conversions ÷ treatment size",
        body: "Share of the messaged group that completed the Target Action.",
    },
    {
        field: "Control Conv. = control conversions ÷ control size",
        body: "Share of the held-out group that converted on its own.",
    },
    {
        field: "Absolute Lift = treatment rate − control rate",
        body: "In percentage points: how many more people per hundred converted because of "
            + "the campaign.",
    },
    {
        field: "95% CI = absolute lift ± 1.96 × SE",
        body: "The range the true lift most likely falls in. A Proven row can show a lower "
            + "bound just below zero — that is expected.",
    },
    {
        field: "Relative Lift = absolute lift ÷ control rate",
        body: "How much better treatment did, relative to control. Shows n/a when the control "
            + "rate is 0%.",
    },
    {
        field: "95% CI for relative lift",
        body: "The absolute-lift range carried through to relative lift, at the same 95% level.",
    },
    {
        field: "P-value = 1 − Φ(absolute lift ÷ SE)",
        body: "How likely this lift would be if the campaign had no effect. Below 0.05 counts "
            + "as significant.",
    },
    {
        field: "Confidence Tier",
        values: V([
            ["Proven", "p < 0.05"],
            ["Directional", "p 0.05 to 0.2"],
            ["Insufficient", "p ≥ 0.2"],
            ["Caution", "Proven, with 0 control conversions"],
        ]),
        body: "Caution means the result is significant but the control rate is too unstable "
            + "to trust.",
    },
    {
        field: "Date Reached",
        body: "When the row most recently became significant. While Live it clears if "
            + "significance is lost; once Ended it is fixed.",
    },
    {
        field: "SRM Check = actual split vs. configured holdout",
        values: V([["Fail", "p < 0.001"]]),
        body: "Fail means randomization looks broken. Investigate before trusting the lift.",
    },
    {
        field: "Statistical Power",
        values: V([["Sufficient", "≥ 80% power"], ["Low", "< 80% power"]]),
        body: "Low means the sample may be too small to ever reach significance, whether or "
            + "not the effect is real.",
    },
    {
        field: "Last Calculated",
        body: "When this row was last recalculated. Rows update as each client's conversion "
            + "file is processed.",
    },
    {
        field: "Rollup = formulas recalculated on pooled totals",
        body: "Conversions and sizes are added up across the group first — not an average of "
            + "campaign results.",
    },
    {
        field: "One row per population + channel + Target Action",
        body: "A channel holdout is a slice of the treatment group held out of one channel. "
            + "It is measured separately from the population holdout and is never pooled into it.",
    },
    {
        field: "Exposed / Held out = the channel's two arms",
        body: "Exposed were served on this channel; held out were not, but were still served "
            + "everywhere else. Both are inside the campaign's treatment group.",
    },
    {
        field: "Exposed Conv. / Held out Conv. = conversions ÷ arm size",
        body: "The same rate as the population table, computed within the channel's two arms. "
            + "The difference between them is this channel's contribution.",
    },
    {
        // Holly's open question on MPN-2044: "What should the Date Range filter: which
        // campaigns appear, or the conversion window?" It filters the conversion window, and
        // as of 7 Oct 2026 that is true of BOTH halves of every rate -- the arm sizes were not
        // bounded at all before, so a narrowed range divided part of a period's conversions by
        // all of history's members. Said here because it changes what the figures mean.
        field: "Date Range = the conversion window",
        body: "Conversions are counted inside the range; each arm is its size at the end of "
            + "it. Narrowing gives a windowed rate, so compare relative lift across ranges "
            + "rather than the rates themselves.",
    },
];

const BY_FIELD = Object.fromEntries(METHODOLOGY.map((entry) => [entry.field, entry]));

/** Which definition a given column points at. Several columns share one. */
const FOR_COLUMN: Record<string, string> = {
    client: "One row per campaign + Target Action",
    campaign: "One row per campaign + Target Action",
    target_action: "One row per campaign + Target Action",
    rollup: "Rollup = formulas recalculated on pooled totals",
    campaign_status: "Campaign Status",
    use_case: "Funnel Stage = mapped from Use Case",
    funnel_stage: "Funnel Stage = mapped from Use Case",
    days_running: "Days Running = today (or end date) − start date",
    treatment_size: "Treatment Size = population − holdout",
    control_size: "Control Size = Held Out group",
    size: "Treatment Size = population − holdout",
    treatment_rate: "Treatment Conv. = treatment conversions ÷ treatment size",
    control_rate: "Control Conv. = control conversions ÷ control size",
    conversion_rate: "Treatment Conv. = treatment conversions ÷ treatment size",
    conversions: "Treatment Conversions = conversions in the messaged group",
    treatment_conversions: "Treatment Conversions = conversions in the messaged group",
    control_conversions: "Control Conversions = conversions in the held-out group",
    absolute_lift: "Absolute Lift = treatment rate − control rate",
    absolute_ci: "95% CI = absolute lift ± 1.96 × SE",
    relative_lift: "Relative Lift = absolute lift ÷ control rate",
    relative_ci: "95% CI for relative lift",
    p_value: "P-value = 1 − Φ(absolute lift ÷ SE)",
    tier: "Confidence Tier",
    date_reached: "Date Reached",
    srm_check: "SRM Check = actual split vs. configured holdout",
    statistical_power: "Statistical Power",
    last_calculated: "Last Calculated",
    date_range: "Date Range = the conversion window",
    channel_grain: "One row per population + channel + Target Action",
    channel_arms: "Exposed / Held out = the channel's two arms",
    channel_rate: "Exposed Conv. / Held out Conv. = conversions ÷ arm size",
};

/** The definition behind a column heading, or undefined when it has none. */
export function methodFor(column: string): MethodEntry | undefined {
    const field = FOR_COLUMN[column];
    return field ? BY_FIELD[field] : undefined;
}
